/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * AGENTS.md Service
 * 
 * API client for AGENTS.md management in the Velocity Playbook System.
 * Provides functions for reading, applying, and managing AGENTS.md files
 * both from product repositories and knowledge mining runs.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  ReadAgentsMdResponseData,
  ReadMiningRunAgentsMdResponseData,
  ApplyAgentsMdResponseData,
  ReadCustomRulesResponseData,
  SaveCustomRulesResponseData,
} from '@shared/types/responses/agents-md';
import type { ListMiningRunsResponseData } from '@shared/types/responses/knowledge-mining';

// ============================================================================
// API Endpoints
// ============================================================================

export const AGENTS_MD_ENDPOINTS = {
  read: '/api/playbooks/agents-md/read',
  apply: '/api/playbooks/agents-md/apply',
  generate: '/api/playbooks/agents-md/generate',
  rules: '/api/playbooks/agents-md/rules',
  miningRunAgentsMd: (jobId: string) => `/api/knowledge-mining/runs/${jobId}/agents-md`,
  miningRuns: '/api/knowledge-mining/runs',
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const AGENTS_MD_QUERY_KEYS = {
  all: ['agents-md'] as const,
  current: (repoUrl: string, repoPath?: string) => ['agents-md', 'current', repoUrl, repoPath] as const,
  customRules: (repoUrl: string, repoPath?: string) => ['agents-md', 'custom-rules', repoUrl, repoPath] as const,
  miningRun: (jobId: string) => ['agents-md', 'mining-run', jobId] as const,
  miningRuns: (productId?: string) => ['agents-md', 'mining-runs', productId] as const,
} as const;

// ============================================================================
// Service Functions
// ============================================================================

/**
 * Read AGENTS.md from product repository root
 * 
 * @param repoUrl - Repository URL
 * @param repoPath - Optional path within repo for monorepos
 * @returns AGENTS.md content and metadata
 */
export async function readCurrentAgentsMd(
  repoUrl: string,
  repoPath?: string
): Promise<ReadAgentsMdResponseData> {
  const params = new URLSearchParams({ repoUrl });
  if (repoPath) {
    params.set('repoPath', repoPath);
  }
  
  const response = await authenticatedFetch(
    `${AGENTS_MD_ENDPOINTS.read}?${params.toString()}`
  );
  return handleApiResponse<ReadAgentsMdResponseData>(response);
}

/**
 * Read AGENTS.md from a mining run
 * 
 * @param jobId - Mining job ID
 * @param productRepoPath - Path to the product repo (required for product-scoped runs)
 * @returns AGENTS.md content from the mining run
 */
export async function readMiningRunAgentsMd(
  jobId: string,
  productRepoPath: string
): Promise<ReadMiningRunAgentsMdResponseData> {
  const params = new URLSearchParams({ productRepoPath });
  
  const response = await authenticatedFetch(
    `${AGENTS_MD_ENDPOINTS.miningRunAgentsMd(jobId)}?${params.toString()}`
  );
  return handleApiResponse<ReadMiningRunAgentsMdResponseData>(response);
}

/**
 * Apply AGENTS.md content to product repository via worktree
 * 
 * Creates a worktree and writes the AGENTS.md content to the product root.
 * The worktree can then be committed and merged.
 * 
 * @param repositoryUrl - Repository URL
 * @param content - AGENTS.md content to apply
 * @param repoPath - Optional path within repo for monorepos
 * @returns Result including worktree path and AGENTS.md path
 */
export async function applyAgentsMd(
  repositoryUrl: string,
  content: string,
  repoPath?: string
): Promise<ApplyAgentsMdResponseData> {
  const response = await authenticatedFetch(AGENTS_MD_ENDPOINTS.apply, {
    method: 'POST',
    body: {
      repositoryUrl,
      content,
      repoPath,
    },
  });
  return handleApiResponse<ApplyAgentsMdResponseData>(response);
}

/**
 * List mining runs, optionally filtered by product
 * 
 * @param productId - Optional product ID to filter runs
 * @param productRepoPath - Optional product repo path (alternative to productId)
 * @returns List of mining runs with metadata
 */
export async function listMiningRuns(
  productId?: string,
  productRepoPath?: string
): Promise<ListMiningRunsResponseData> {
  const params = new URLSearchParams();
  if (productId) {
    params.set('productId', productId);
  }
  if (productRepoPath) {
    params.set('productRepoPath', productRepoPath);
  }
  
  const url = params.toString() 
    ? `${AGENTS_MD_ENDPOINTS.miningRuns}?${params.toString()}`
    : AGENTS_MD_ENDPOINTS.miningRuns;
    
  const response = await authenticatedFetch(url);
  return handleApiResponse<ListMiningRunsResponseData>(response);
}

// ============================================================================
// Custom Rules Functions
// ============================================================================

/**
 * Read custom rules from data/product/rules.md
 * 
 * @param repoUrl - Repository URL
 * @param repoPath - Optional path within repo for monorepos
 * @returns Custom rules content and metadata
 */
export async function readCustomRules(
  repoUrl: string,
  repoPath?: string
): Promise<ReadCustomRulesResponseData> {
  const params = new URLSearchParams({ repoUrl });
  if (repoPath) {
    params.set('repoPath', repoPath);
  }
  
  const response = await authenticatedFetch(
    `${AGENTS_MD_ENDPOINTS.rules}?${params.toString()}`
  );
  return handleApiResponse<ReadCustomRulesResponseData>(response);
}

/**
 * Save custom rules to data/product/rules.md
 * 
 * @param repoUrl - Repository URL
 * @param content - Rules content to save
 * @param repoPath - Optional path within repo for monorepos
 * @returns Save result
 */
export async function saveCustomRules(
  repoUrl: string,
  content: string,
  repoPath?: string
): Promise<SaveCustomRulesResponseData> {
  const response = await authenticatedFetch(AGENTS_MD_ENDPOINTS.rules, {
    method: 'PUT',
    body: {
      repoUrl,
      content,
      repoPath,
    },
  });
  return handleApiResponse<SaveCustomRulesResponseData>(response);
}
